import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Icon } from '@/components/Icon';
import { saveSignal, type SignalDraft } from '@/lib/api';
import type { Signal, SignalFrequency, SignalScope } from '@/lib/signals';
import { BODY_META, type BodyType } from '@/lib/types';
import { isProTier } from '@/lib/tier';
import { useTier } from '@/session/SessionProvider';
import './SignalSheet.css';

/**
 * The Signal creation and edit sheet.
 *
 * **One sheet, opened from four places** — Search's "Save as signal", Signal Feed, the
 * Signals list, and Topic Trends' "Track a topic". Creating from any of them must look
 * identical, which is why this component is shared rather than reimplemented per surface.
 *
 * Topic mode reuses the same form but creates a **Topic**, which is a different object from a
 * Signal: a Topic counts meetings over time for a chart, a Signal finds documents for a feed.
 * Same sheet, different collection — do not merge the two on the strength of the shared UI.
 */

/**
 * The categories that actually carry volume in the corpus, verified against Atlas 08/15/2026.
 *
 * The seed prompt describes a "strict 10", but the live taxonomy has drifted to 20 distinct
 * values — the summary prompt is operator-editable, exactly as `DATA_GROUNDING.md` warned.
 * The ten below are every category with material volume; the remaining ten are drift with
 * negligible counts (Technology 17 items, Agriculture 24, Land Acquisition 17).
 *
 * Two things worth knowing when reading a filtered feed:
 *   - **Housing** (7,464 items) is real and was not in the seed list. It is included here.
 *   - **Governance is 46% of the entire corpus** (1.44M of 3.1M items), so filtering to it
 *     barely narrows anything. That is a taxonomy problem upstream, not a UI one.
 *
 * "Miscellaneous" exists upstream and is never surfaced to a user.
 */
const CATEGORIES = [
  'Community',
  'Education',
  'Environment',
  'Finance',
  'Governance',
  'Housing',
  'Infrastructure',
  'Public Health',
  'Public Safety',
  'Urban Planning',
];

const FREQUENCIES: { key: SignalFrequency; label: string }[] = [
  { key: 'realtime', label: 'Real-time' },
  { key: 'daily', label: 'Daily digest' },
  { key: 'weekly', label: 'Weekly' },
];

export interface SheetSeed {
  /** Present when editing; absent when creating. */
  id?: string;
  name?: string;
  terms?: string;
  scope?: SignalScope;
  bodies?: BodyType[];
  categories?: string[];
  frequency?: SignalFrequency;
  email?: boolean;
  whatsApp?: boolean;
  /** Topic mode creates a Topic for Topic Trends, not a Signal. */
  mode?: 'signal' | 'topic';
}

export function SignalSheet({
  seed,
  followedTownCount,
  corpusSummary,
  onClose,
  onSaved,
}: {
  seed: SheetSeed;
  followedTownCount: number;
  /** "over 3,800 places · 5 countries" for the "Everywhere" scope hint. */
  corpusSummary: string;
  onClose: () => void;
  onSaved?: (signal: Signal) => void;
}) {
  const tier = useTier();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const isTopic = seed.mode === 'topic';
  const isEdit = Boolean(seed.id);
  const pro = isProTier(tier);

  const [name, setName] = useState(seed.name ?? '');
  const [terms, setTerms] = useState(seed.terms ?? '');
  const [scope, setScope] = useState<SignalScope>(seed.scope ?? 'mytowns');
  const [bodies, setBodies] = useState<BodyType[]>(seed.bodies ?? ['municipal']);
  const [categories, setCategories] = useState<string[]>(seed.categories ?? []);
  const [frequency, setFrequency] = useState<SignalFrequency>(seed.frequency ?? 'daily');
  const [email, setEmail] = useState(seed.email ?? true);
  const [whatsApp, setWhatsApp] = useState(seed.whatsApp ?? false);

  useEffect(() => {
    firstFieldRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: (draft: SignalDraft) => saveSignal(draft),
    onSuccess: (signal) => {
      void queryClient.invalidateQueries({ queryKey: ['signals'] });
      void queryClient.invalidateQueries({ queryKey: ['signal-feed'] });
      onSaved?.(signal);
      onClose();
    },
  });

  const canSave = terms.trim().length > 0;

  /** A rough weekly volume, so nobody creates a Signal that will bury them. */
  const estimate = 3 + categories.length * 2 + (scope === 'corpus' ? 9 : scope === 'states' ? 4 : 1);

  const SCOPES: { key: SignalScope; label: string; hint: string; locked: boolean }[] = [
    {
      key: 'mytowns',
      label: 'What I follow',
      hint: `${followedTownCount} followed`,
      locked: false,
    },
    { key: 'states', label: 'Selected states', hint: 'NJ + PA', locked: !pro },
    { key: 'corpus', label: 'Everywhere', hint: corpusSummary, locked: !pro },
  ];

  function toggle<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
  }

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={isTopic ? 'Track a topic' : 'Signal'}>
      <header className="sheet__head">
        <div>
          <p className="u-meta">
            <Icon name={isTopic ? 'trends' : 'signals'} />{' '}
            {isTopic ? 'Topic Trends' : 'Signals'}
          </p>
          <h2 className="sheet__title">
            {isTopic ? 'Track a topic' : isEdit ? 'Edit signal' : 'New signal'}
          </h2>
        </div>
        <button type="button" className="sheet__close" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="sheet__body">
        <label className="sheet__field">
          <span className="sheet__label">
            {isTopic ? 'Count meetings that discuss' : 'Tell me when a meeting mentions'}
          </span>
          <input
            ref={firstFieldRef}
            type="text"
            value={terms}
            placeholder="battery storage OR solar array"
            onChange={(e) => setTerms(e.target.value)}
          />
        </label>

        <label className="sheet__field">
          <span className="sheet__label">Name {!isEdit && <em>optional</em>}</span>
          <input
            type="text"
            value={name}
            placeholder={terms || 'Untitled'}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <fieldset className="sheet__group">
          <legend className="sheet__label">Search in</legend>
          <div className="sheet__options">
            {SCOPES.map((option) => (
              <button
                key={option.key}
                type="button"
                className="sheet__option"
                data-selected={scope === option.key}
                data-locked={option.locked}
                onClick={() => {
                  if (option.locked) {
                    onClose();
                    void navigate({ to: '/pricing' });
                    return;
                  }
                  setScope(option.key);
                }}
              >
                {option.locked && <Icon name="lock" />}
                <span className="sheet__optionLabel">{option.label}</span>
                <span className="sheet__optionHint">{option.hint}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="sheet__group">
          <legend className="sheet__label">Body type</legend>
          <div className="sheet__chips">
            {(Object.keys(BODY_META) as BodyType[]).map((key) => {
              const meta = BODY_META[key];
              return (
                <button
                  key={key}
                  type="button"
                  className="sheet__chip"
                  data-selected={bodies.includes(key)}
                  data-soon={meta.comingSoon}
                  disabled={meta.comingSoon}
                  title={meta.comingSoon ? 'County and State coverage is coming' : undefined}
                  onClick={() => setBodies((b) => toggle(b, key))}
                >
                  {meta.short}
                  {meta.comingSoon && <span className="sheet__soon">soon</span>}
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="sheet__group">
          <legend className="sheet__label">
            Category <em>optional</em>
          </legend>
          <div className="sheet__chips">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                type="button"
                className="sheet__chip"
                data-selected={categories.includes(category)}
                onClick={() => setCategories((c) => toggle(c, category))}
              >
                {category}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Delivery is a setting ON the Signal, not a separate object — which is why it lives
            in this sheet rather than on a Notifications page. */}
        {!isTopic && (
          <>
            <fieldset className="sheet__group">
              <legend className="sheet__label">How often</legend>
              <div className="sheet__chips">
                {FREQUENCIES.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className="sheet__chip"
                    data-selected={frequency === option.key}
                    onClick={() => setFrequency(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="sheet__group">
              <legend className="sheet__label">Where to send it</legend>
              <div className="sheet__chips">
                <button
                  type="button"
                  className="sheet__chip"
                  data-selected={email}
                  onClick={() => setEmail((v) => !v)}
                >
                  Email
                </button>
                <button
                  type="button"
                  className="sheet__chip"
                  data-selected={whatsApp}
                  data-locked={!pro}
                  onClick={() => {
                    if (!pro) {
                      onClose();
                      void navigate({ to: '/pricing' });
                      return;
                    }
                    setWhatsApp((v) => !v);
                  }}
                >
                  {!pro && <Icon name="lock" />}
                  WhatsApp
                </button>
              </div>
              {whatsApp && (
                /* WhatsApp alerts are business-initiated, so outside a 24-hour reply window
                   they can only be a message format WhatsApp has pre-approved. The alert
                   fills in the blanks; it cannot be free-form matched text. Saying so here
                   prevents the reasonable assumption that WhatsApp mirrors the email. */
                <p className="sheet__note">
                  WhatsApp alerts use a fixed message format: the town, the body, and the
                  matched item, with a link to the full recap. Email carries the full passage.
                </p>
              )}
            </fieldset>
          </>
        )}

        <p className="sheet__estimate">
          {isTopic
            ? 'Twelve months of history load immediately — the meetings are already processed.'
            : `≈ ${estimate} matches a week at this scope`}
        </p>

        {save.isError && (
          <p className="sheet__error" role="alert">
            Couldn’t save that. Try again.
          </p>
        )}
      </div>

      <footer className="sheet__foot">
        <button type="button" className="sheet__cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="sheet__save"
          disabled={!canSave || save.isPending}
          onClick={() =>
            save.mutate({
              id: seed.id,
              name: (name || terms).trim(),
              terms: terms.trim(),
              scope,
              bodies: bodies.length > 0 ? bodies : ['municipal'],
              categories,
              frequency,
              email,
              whatsApp,
            })
          }
        >
          {save.isPending
            ? 'Saving…'
            : isTopic
              ? 'Start tracking'
              : isEdit
                ? 'Save changes'
                : 'Create signal'}
        </button>
      </footer>
    </div>
  );
}
